<template>
  <b-overlay :show="isLoading || selectAllTradiesBusy" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>

    <transition name="bounce" @after-enter="positionLockOverlays">
      <b-container class="py-5 custom-container container">
        <b-container class="custom-container mt-5">
          <b-breadcrumb
            :items="breadcrumbs"
            class="pl-0 pt-5 mt-5 bg-transparent"
          ></b-breadcrumb>

          <!-- Header -->
          <b-row class="project-header-row">
            <b-col class="project-title-col">
              <h2 class="font-weight-bold project-title">
                {{ project ? project.name : '' }}
              </h2>
              <p v-if="sharerLabel" class="text-muted mb-0 project-shared-by">
                Shared with you by <strong>{{ sharerLabel }}</strong>
              </p>
            </b-col>

            <b-col class="text-right align-right project-actions-col">
              <b-row
                class="justify-content-end flex-nowrap flex-md-wrap align-items-center"
              >
                <b-col cols="auto" md="4" class="px-1 px-md-2 mb-2 mb-md-0">
                  <b-button
                    variant="outline-secondary"
                    class="bg-white text-dark header-action-btn"
                    title="Back to Projects"
                    @click="gotoProjects()"
                  >
                    <b-icon-arrow-left
                      class="header-action-icon d-md-none"
                      aria-hidden="true"
                    />
                    <span class="d-none d-md-inline">Back to Projects</span>
                    <span class="sr-only d-md-none">Back to Projects</span>
                  </b-button>
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-alert v-if="loadError" show variant="danger" class="mt-3">
            {{ loadError }}
          </b-alert>

          <b-alert show class="mt-3 read-only-banner">
            <b-icon-eye class="align-middle mr-1" />
            <span class="align-middle">
              Read-only view - no changes can be made from here.
            </span>
          </b-alert>

          <!-- Project Details -->
          <b-row>
            <b-col cols="12" sm="12" class="d-flex flex-column">
              <b-container fluid>
                <!-- Overview | Files, same as the project page. -->
                <project-page-tabs
                  class="mt-4"
                  :active="activeTab"
                  :showFiles="canViewProjectFiles"
                  @change="setTab"
                />

                <b-card
                  class="file-details"
                  :class="[
                    canViewProjectFiles ? 'mt-3' : 'mt-4',
                    { 'd-none': !showOverviewTab },
                  ]"
                >
                  <h3 class="my-4 font-weight-bold">Project Details</h3>
                  <b-card-body class="pl-0 pr-0 align-items-center">
                    <!-- Project Name -->
                    <b-row class="mb-4 project-name-detail-row" no-gutters>
                      <!-- eslint-disable-next-line -->
                      <b-col cols="auto" class="font-weight-bold text-nowrap project-name-detail-label">Project</b-col>
                      <b-col class="gray--2 project-name-detail-value">
                        {{ project ? project.name : '' }}
                      </b-col>
                    </b-row>

                    <!-- Tradies -->
                    <b-row class="tradie-row">
                      <!-- eslint-disable-next-line -->
                      <b-col cols="4" sm="4" class="font-weight-bold text-nowrap section-title">Tradies:</b-col>
                      <b-col cols="12" class="tradie-content-col">
                        <b-container fluid class="px-0">
                          <b-row
                            align-h="end"
                            align-v="end"
                            class="tradie-toolbar-row"
                          >
                            <b-col
                              cols="12"
                              md="4"
                              class="justify-content-start align-items-center mb-3 pr-md-5 tradie-search"
                            >
                              <b>Search (Name, Email, Role):</b>
                              <b-form-input
                                v-model="search"
                                placeholder="Type to search"
                                debounce="500"
                              />
                            </b-col>
                            <b-col
                              class="d-flex flex-wrap justify-content-end align-items-end tradie-toolbar-actions"
                              :class="{
                                'tradie-toolbar-actions--supervisor':
                                  isSupervisorTradie,
                              }"
                            >
                              <b-button
                                v-if="canDownloadTradieFiles"
                                class="px-3 py-0 mb-3 mr-3 tradie-action-btn"
                                style="white-space: nowrap"
                                @click="downloadTradies"
                              >
                                <b-icon-download class="align-middle" />
                                <span class="align-middle d-none d-lg-inline">
                                  Download Tradies
                                </span>
                                <span class="align-middle d-lg-none">
                                  Download
                                </span>
                              </b-button>
                            </b-col>
                          </b-row>
                        </b-container>

                        <!-- Mobile: stacked tradie rows (hidden on md+) -->
                        <div class="d-md-none mobile-tradie-list">
                          <div
                            v-for="item in visibleTradies"
                            :key="item.id"
                            class="mobile-tradie-item"
                            :class="[
                              getRowClass(item),
                              { 'is-selected': isTradieSelected(item.id) },
                            ]"
                            @click="viewTradie(item)"
                          >
                            <b-icon-check-circle-fill
                              v-if="item.project_approved"
                              class="text-success mobile-tradie__tick"
                              title="Project approved"
                            />
                            <div class="mobile-tradie__body">
                              <span
                                class="mobile-tradie__name"
                                :class="{
                                  'text-muted': isTradieTokenLocked(item),
                                }"
                              >
                                {{
                                  item.full_name === ''
                                    ? '-----'
                                    : item.full_name
                                }}
                              </span>
                              <div
                                v-if="canShowTradieField(item)"
                                class="mobile-tradie__meta"
                              >
                                <span v-if="item.email">{{ item.email }}</span>
                                <span
                                  v-if="
                                    item.trade_names && item.trade_names.length
                                  "
                                >
                                  · {{ shortenString(item.trade_names, 20) }}
                                </span>
                                <span
                                  v-if="item.need_attention"
                                  class="text-danger"
                                >
                                  · Needs attention
                                </span>
                              </div>
                              <div
                                v-else
                                class="mobile-tradie__meta mobile-tradie__meta--lock d-flex align-items-center"
                              >
                                <img
                                  src="@/assets/images/lock.png"
                                  alt="Locked"
                                  class="mobile-tradie__lock-icon mr-1"
                                />
                                {{ tradieLockMessage() }}
                              </div>
                            </div>
                            <div
                              v-if="canShowTradieField(item)"
                              class="mobile-tradie__select"
                              @click.stop
                            >
                              <b-button
                                :style="{
                                  backgroundColor: isTradieSelected(item.id)
                                    ? '#28a745'
                                    : 'white',
                                }"
                                class="mobile-tradie__select-btn"
                                @click="toggleTradieSelection(item.id)"
                              >
                                <b-icon-check2
                                  v-if="isTradieSelected(item.id)"
                                />
                              </b-button>
                            </div>
                          </div>
                          <div
                            v-if="!visibleTradies || !visibleTradies.length"
                            class="text-center py-4 text-muted"
                          >
                            No tradies found.
                          </div>
                        </div>

                        <!-- Tradies Table (desktop) -->
                        <div class="table-wrapper d-none d-md-block">
                          <b-table
                            v-if="tradies"
                            :items="visibleTradies"
                            :fields="tradieFields"
                            show-empty
                            responsive
                            hover
                            :tbody-tr-class="getRowClass"
                            class="tradies-table"
                            @row-clicked="viewTradie"
                          >
                            <template #cell(project_approved)="{ item }">
                              <b-icon-check-circle-fill
                                v-if="item.project_approved"
                                v-b-tooltip.hover
                                title="Project approved"
                                class="text-success project-approved-tick"
                                aria-label="Project approved"
                              />
                            </template>

                            <template #cell(full_name)="data">
                              <div class="cell-content-wrapper">
                                <span
                                  :class="{
                                    'text-muted': isTradieTokenLocked(
                                      data.item,
                                    ),
                                  }"
                                >
                                  {{
                                    data.item.full_name == ''
                                      ? '-----'
                                      : data.item.full_name
                                  }}
                                </span>
                                <div
                                  v-if="isTradieTokenLocked(data.item)"
                                  class="lock-overlay"
                                  :ref="`lock-over-lay-${data.item.id}`"
                                >
                                  <img
                                    src="@/assets/images/lock.png"
                                    alt="Locked"
                                    class="lock-icon mr-2"
                                  />
                                  <span>{{ tradieLockMessage() }}</span>
                                </div>
                              </div>
                            </template>

                            <template #cell(email)="data">
                              <span v-if="canShowTradieField(data.item)">{{
                                data.item.email
                              }}</span>
                            </template>

                            <template #cell(trade_names)="data">
                              <span
                                v-if="canShowTradieField(data.item)"
                                v-b-tooltip.html
                                :title="getTooltipText(data.item.trade_names)"
                              >
                                {{ shortenString(data.item.trade_names, 30) }}
                              </span>
                            </template>

                            <template #cell(proof_of_id_files)="data">
                              <span
                                v-if="canShowTradieField(data.item)"
                                v-b-tooltip.html
                                :title="
                                  getTooltipText(data.item.proof_of_id_files)
                                "
                              >
                                {{ shortenString(data.item.proof_of_id_files) }}
                              </span>
                            </template>

                            <template #cell(need_attention)="{ item }">
                              <span v-if="canShowTradieField(item)">
                                {{ item.need_attention ? 'Yes' : 'No' }}
                              </span>
                            </template>

                            <template #head(select)>
                              <div class="text-center w-100">
                                <span
                                  :style="{
                                    cursor: selectAllTradiesBusy
                                      ? 'wait'
                                      : 'pointer',
                                    'user-select': 'none',
                                    padding: '2px 6px',
                                    border: '1px solid #ddd',
                                    'border-radius': '3px',
                                    display: 'inline-block',
                                    opacity: selectAllTradiesBusy ? 0.5 : 1,
                                  }"
                                  @click="
                                    !selectAllTradiesBusy &&
                                      toggleSelectAllTradies()
                                  "
                                >
                                  Select All
                                </span>
                              </div>
                            </template>

                            <template #cell(select)="{ item }">
                              <div
                                class="text-center d-flex justify-content-center align-items-center"
                              >
                                <b-button
                                  v-if="canShowTradieField(item)"
                                  :style="{
                                    backgroundColor: isTradieSelected(item.id)
                                      ? '#28a745'
                                      : 'white',
                                  }"
                                  style="
                                    height: 32px;
                                    width: 32px;
                                    border-radius: 8px;
                                    border: 1px solid grey;
                                    padding-left: 5px !important;
                                  "
                                  class="px-2"
                                  @click.stop="toggleTradieSelection(item.id)"
                                >
                                  <b-icon-check2
                                    v-if="isTradieSelected(item.id)"
                                  />
                                </b-button>
                              </div>
                            </template>
                          </b-table>
                        </div>

                        <b-pagination
                          v-if="rows > perPage"
                          v-model="currentPage"
                          :total-rows="rows"
                          :per-page="perPage"
                          :current-page="currentPage"
                          :limit="paginationLimit"
                          align="center"
                          class="tradies-pagination"
                        />

                        <p
                          v-if="tradiesMeta"
                          align="center"
                          class="tradie-count"
                        >
                          Showing {{ tradies.length }} out of total of
                          {{ tradiesMeta.total }} Tradies
                        </p>
                      </b-col>
                    </b-row>

                    <!-- Needing Attention -->
                    <template v-if="attentionTradiesAll.length > 0">
                      <div class="section-separator"></div>

                      <b-row class="bg-highlight py-4 attention-row">
                        <b-col cols="12" class="font-weight-bold section-title">
                          Needing Attention:
                        </b-col>
                        <b-col cols="12" class="attention-cards-col">
                          <!-- Independent of the Tradies table's own search
                               box above — filters only this band. -->
                          <b-row>
                            <b-col
                              cols="12"
                              md="4"
                              class="mb-3 tradie-search band-search"
                            >
                              <b>Search (Name, Email, Role):</b>
                              <b-form-input
                                v-model="attentionSearch"
                                placeholder="Type to search"
                                debounce="500"
                              />
                            </b-col>
                          </b-row>

                          <!-- Mobile: stacked tradie rows (hidden on md+) -->
                          <div class="d-md-none mobile-tradie-list">
                            <div
                              v-for="item in attentionTradiesPage"
                              :key="item.id"
                              class="mobile-tradie-item"
                              :class="getRowClass(item)"
                              @click="viewTradie(item)"
                            >
                              <div class="mobile-tradie__body">
                                <div class="mobile-tradie__name-row">
                                  <span
                                    class="mobile-tradie__name"
                                    :class="{
                                      'text-muted': isTradieTokenLocked(item),
                                    }"
                                  >
                                    {{
                                      item.full_name === ''
                                        ? '-----'
                                        : item.full_name
                                    }}
                                  </span>
                                  <b-icon-check-circle-fill
                                    v-if="item.project_approved"
                                    class="text-success mobile-tradie__tick"
                                    title="Project approved"
                                  />
                                </div>
                                <div
                                  v-if="canShowTradieField(item)"
                                  class="mobile-tradie__meta"
                                >
                                  <span v-if="item.email">{{
                                    item.email
                                  }}</span>
                                  <span
                                    v-if="
                                      item.trade_names &&
                                      item.trade_names.length
                                    "
                                  >
                                    · {{ shortenString(item.trade_names, 20) }}
                                  </span>
                                </div>
                                <ul
                                  v-if="
                                    canShowTradieField(item) &&
                                    needAttentionReasons(item).length
                                  "
                                  class="mobile-tradie__reasons"
                                >
                                  <li
                                    v-for="(reason, i) in needAttentionReasons(
                                      item,
                                    )"
                                    :key="i"
                                  >
                                    {{ reason }}
                                  </li>
                                </ul>
                                <div
                                  v-if="!canShowTradieField(item)"
                                  class="mobile-tradie__meta mobile-tradie__meta--lock d-flex align-items-center"
                                >
                                  <img
                                    src="@/assets/images/lock.png"
                                    alt="Locked"
                                    class="mobile-tradie__lock-icon mr-1"
                                  />
                                  {{ tradieLockMessage() }}
                                </div>
                              </div>
                              <div
                                v-if="canShowTradieField(item)"
                                class="mobile-tradie__select"
                                @click.stop
                              >
                                <b-button
                                  :style="{
                                    backgroundColor: isTradieSelected(item.id)
                                      ? '#28a745'
                                      : 'white',
                                  }"
                                  class="mobile-tradie__select-btn"
                                  @click="toggleTradieSelection(item.id)"
                                >
                                  <b-icon-check2
                                    v-if="isTradieSelected(item.id)"
                                  />
                                </b-button>
                              </div>
                            </div>
                            <div
                              v-if="!attentionTradies.length"
                              class="text-center py-4 text-muted"
                            >
                              There are no records to show.
                            </div>
                          </div>

                          <!-- Needing Attention Table (desktop) -->
                          <div class="table-wrapper d-none d-md-block">
                            <b-table
                              id="attention-tradies-table"
                              :items="attentionTradiesPage"
                              :fields="attentionTradieFields"
                              show-empty
                              responsive
                              hover
                              :tbody-tr-class="getRowClass"
                              class="tradies-table"
                              @row-clicked="viewTradie"
                            >
                              <template #cell(project_approved)="{ item }">
                                <b-icon-check-circle-fill
                                  v-if="item.project_approved"
                                  v-b-tooltip.hover
                                  title="Project approved"
                                  class="text-success project-approved-tick"
                                  aria-label="Project approved"
                                />
                              </template>

                              <template #cell(full_name)="data">
                                <div class="cell-content-wrapper">
                                  <span
                                    :class="{
                                      'text-muted': isTradieTokenLocked(
                                        data.item,
                                      ),
                                    }"
                                  >
                                    {{
                                      data.item.full_name == ''
                                        ? '-----'
                                        : data.item.full_name
                                    }}
                                  </span>
                                  <div
                                    v-if="isTradieTokenLocked(data.item)"
                                    class="lock-overlay"
                                    :ref="`lock-over-lay-${data.item.id}`"
                                  >
                                    <img
                                      src="@/assets/images/lock.png"
                                      alt="Locked"
                                      class="lock-icon mr-2"
                                    />
                                    <span>{{ tradieLockMessage() }}</span>
                                  </div>
                                </div>
                              </template>

                              <template #cell(email)="data">
                                <span v-if="canShowTradieField(data.item)">{{
                                  data.item.email
                                }}</span>
                              </template>

                              <template #cell(trade_names)="data">
                                <span
                                  v-if="canShowTradieField(data.item)"
                                  v-b-tooltip.html
                                  :title="getTooltipText(data.item.trade_names)"
                                >
                                  {{ shortenString(data.item.trade_names, 30) }}
                                </span>
                              </template>

                              <template #cell(need_attention)="{ item }">
                                <span v-if="canShowTradieField(item)">
                                  {{ item.need_attention ? 'Yes' : 'No' }}
                                </span>
                              </template>

                              <template
                                #cell(need_attention_reasons)="{ item }"
                              >
                                <span v-if="!canShowTradieField(item)"></span>
                                <span
                                  v-else-if="!needAttentionReasons(item).length"
                                  class="text-muted"
                                >
                                  –
                                </span>
                                <ul
                                  v-else
                                  class="attention-reasons text-danger"
                                >
                                  <li
                                    v-for="(reason, i) in needAttentionReasons(
                                      item,
                                    )"
                                    :key="i"
                                    class="attention-reason"
                                  >
                                    {{ reason }}
                                  </li>
                                </ul>
                              </template>
                            </b-table>
                          </div>

                          <b-pagination
                            v-if="attentionTradies.length > perPage3"
                            v-model="currentPage3"
                            :total-rows="attentionTradies.length"
                            :per-page="perPage3"
                            :limit="paginationLimit"
                            aria-controls="attention-tradies-table"
                            align="center"
                            class="tradies-pagination"
                          />

                          <p
                            v-if="attentionTradiesAll.length"
                            align="center"
                            class="tradie-count"
                          >
                            Showing {{ attentionTradiesPage.length }} out of
                            total of {{ attentionTradiesAll.length }} Needing
                            Attention
                          </p>
                        </b-col>
                      </b-row>
                    </template>

                    <!-- Separator -->
                    <div class="section-separator"></div>

                    <!-- Expiring Files (in 30 days) -->
                    <b-row class="mobile-flush-row">
                      <b-col
                        cols="12"
                        class="font-weight-bold title-nowrap-mobile section-title"
                      >
                        Expiring Files (in 30 days):
                      </b-col>
                      <b-col cols="12">
                        <expiring-tradie-list
                          v-if="projectId"
                          :projectId="projectId"
                          :readOnly="true"
                          :hideCompanyFiles="true"
                          :lockMessage="tradieLockMessage()"
                        />
                      </b-col>
                    </b-row>
                  </b-card-body>
                </b-card>

                <!-- The owner's project documents, read-only. The API hides
                     archived files and marks the response read-only, so the
                     panel shows no write controls or Archived tab. -->
                <project-files-panel
                  v-if="projectId && canViewProjectFiles"
                  v-show="showFilesTab"
                  class="mt-3"
                  :projectId="projectId"
                />
              </b-container>
            </b-col>
          </b-row>
        </b-container>
      </b-container>
    </transition>
  </b-overlay>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ExpiringTradieList from '@/components/lists/ExpiringTradieList'
import ProjectFilesPanel from '@/components/projects/ProjectFilesPanel'
import ProjectPageTabs from '@/components/projects/ProjectPageTabs'
import projectPageTabs from '@/mixins/projectPageTabs'
import { applyTokenExpiryFromListTradies } from '@/utils/tradieListToken'

export default {
  name: 'SharedViewPage',

  // Overview | Files tabs, kept in the URL as ?tab=files.
  mixins: [projectPageTabs],

  components: {
    ExpiringTradieList,
    ProjectFilesPanel,
    ProjectPageTabs,
  },

  props: {
    shareId: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      share: null,
      projectId: null,
      loadError: '',

      isLoading: false,
      selectAllTradiesBusy: false,

      perPage: 10,
      currentPage: 1,
      search: '',

      // Needing Attention band: its own client-side search + pagination,
      // independent of the Tradies table above (mirrors ViewPage.vue).
      perPage3: 10,
      currentPage3: 1,
      attentionSearch: '',

      // Full, search-independent tradie list backing the Needing Attention
      // band so it isn't limited to the main Tradies table's current page
      // (mirrors ViewPage.vue's attentionTradiesSource).
      attentionTradiesSource: [],

      paginationLimit: 5,

      // Drives the responsive column reveal in tradieFields (< 576 = phone).
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,

      tokenExpiryDates: {},
      selectedTradies: [],
    }
  },

  computed: {
    ...mapGetters('project', ['project', 'tradies', 'tradiesMeta']),
    ...mapGetters('auth', ['user', 'hasPermission']),

    canDownloadTradieFiles() {
      if (!this.user || this.user.user_type !== 'business_admin') return true
      return this.hasPermission('tradies.files.download')
    },

    // Panel is read-only here regardless; this only hides it from roles that
    // can't view project files at all.
    canViewProjectFiles() {
      return this.hasPermission('projects.files.view')
    },

    isSupervisorTradie() {
      return !!(
        this.user &&
        this.user.user_type === 'tradie' &&
        this.user.is_supervisor
      )
    },

    breadcrumbs() {
      return [
        { text: 'Projects', to: '/projects' },
        { text: this.project ? this.project.name : '' },
      ]
    },

    rows() {
      return this.tradiesMeta ? this.tradiesMeta.total : 0
    },

    sharerLabel() {
      if (!this.share) return ''
      const company = this.share.sharer_company_name
      const person =
        this.share.sharer && this.share.sharer.full_name
          ? this.share.sharer.full_name
          : ''
      if (company && person) return `${company} (${person})`
      return company || person || ''
    },

    visibleTradies() {
      return this.tradies || []
    },

    /**
     * Every tradie on the project that needs attention, from a dedicated
     * search-independent fetch (attentionTradiesSource) so the band isn't
     * limited to the main Tradies table's current page. Falls back to the
     * loaded page while that fetch is in flight or if it failed.
     */
    attentionTradiesAll() {
      const source =
        this.attentionTradiesSource && this.attentionTradiesSource.length
          ? this.attentionTradiesSource
          : this.tradies || []
      return source.filter((x) => x && x.need_attention)
    },

    /**
     * `attentionTradiesAll` further narrowed by this band's own search box
     * (independent of the Tradies table's `search`).
     */
    attentionTradies() {
      const term = (this.attentionSearch || '').trim().toLowerCase()
      if (!term) return this.attentionTradiesAll
      return this.attentionTradiesAll.filter((x) => {
        const haystack = [x.full_name, x.email, x.trade_names]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        return haystack.includes(term)
      })
    },

    /** One page's worth of `attentionTradies`, for the band's own pager. */
    attentionTradiesPage() {
      const start = (this.currentPage3 - 1) * this.perPage3
      return this.attentionTradies.slice(start, start + this.perPage3)
    },

    /**
     * Tradies table columns. Progressively reveals columns as the viewport
     * grows so the table stays usable at 320px and expands into the full
     * table by lg. Mirrors ViewPage.vue (minus the Remove column — this
     * view is read-only):
     *   - xs (<576):   tick, Name, Select
     *   - sm (>=576):  + Email
     *   - md (>=768):  + Roles
     *   - lg (>=992):  + Proof of ID, + Needing Attention  (= full table)
     */
    tradieFields() {
      const short = this.windowWidth < 576
      return [
        {
          key: 'project_approved',
          label: '',
          thClass: 'project-approved-th',
          tdClass: 'project-approved-td',
        },
        {
          key: 'full_name',
          label: short ? 'Name' : 'Full Name',
          thClass: 'text-nowrap',
          tdClass: 'tradie-name-cell',
        },
        {
          key: 'email',
          label: 'Email',
          thClass: 'text-nowrap d-none d-sm-table-cell',
          tdClass: 'd-none d-sm-table-cell',
          formatter: (value) => {
            if (!value) return '-'
            return value.length > 30 ? value.substring(0, 30) + '...' : value
          },
        },
        {
          key: 'trade_names',
          label: 'Roles',
          thClass: 'text-nowrap d-none d-md-table-cell',
          tdClass: 'text-nowrap d-none d-md-table-cell',
        },
        {
          key: 'proof_of_id_files',
          label: 'Proof of ID',
          thClass: 'text-nowrap d-none d-lg-table-cell',
          tdClass: 'd-none d-lg-table-cell',
        },
        {
          key: 'need_attention',
          label: 'Needing Attention',
          thClass: 'text-nowrap d-none d-lg-table-cell',
          tdClass: 'd-none d-lg-table-cell',
        },
        {
          key: 'select',
          label: 'Select',
          thClass: 'text-center text-nowrap',
          tdClass: 'text-center',
        },
      ]
    },

    /**
     * Needing Attention table columns: the main tradies table minus Proof of
     * ID and the Select column (bulk actions aren't offered from that band),
     * plus a "Reason" column — the why lives only in this band, not the main
     * tradies table.
     */
    attentionTradieFields() {
      const fields = this.tradieFields.filter(
        (field) => !['proof_of_id_files', 'select'].includes(field.key),
      )
      const reasonField = {
        key: 'need_attention_reasons',
        label: 'Reason',
        // Shown from md (768) — the band's whole point — not just lg. The
        // 768–1400 compact media query keeps it fitting without a scrollbar.
        thClass: 'text-nowrap d-none d-md-table-cell',
        tdClass: 'd-none d-md-table-cell attention-reason-cell',
      }
      const at = fields.findIndex((f) => f.key === 'need_attention')
      if (at === -1) fields.push(reasonField)
      else fields.splice(at + 1, 0, reasonField)
      return fields
    },
  },

  watch: {
    async search() {
      this.currentPage = 1
      await this.fetchTradies()
    },
    async currentPage() {
      await this.fetchTradies()
    },
    attentionSearch() {
      this.currentPage3 = 1
    },
  },

  methods: {
    ...mapActions('project', [
      'getProject',
      'getProjectShare',
      'getTradiesByProjectId',
    ]),

    onResize() {
      this.windowWidth = window.innerWidth
    },

    async fetchAll() {
      this.isLoading = true
      this.loadError = ''
      try {
        const share = await this.getProjectShare(this.shareId)
        this.share = share || null
        const projectId =
          share && share.project && share.project.id ? share.project.id : null
        if (!projectId) {
          this.loadError = 'This shared project is no longer available.'
          return
        }
        this.projectId = projectId
        await this.getProject({
          id: projectId,
          page: this.currentPage,
          limit: this.perPage,
        })
        applyTokenExpiryFromListTradies(this, this.tradies)
        this.fetchAttentionTradies()
      } catch (err) {
        const status = err && err.status
        if (status === 403) {
          this.loadError =
            "You don't have access to this shared project. The share may have been removed or expired."
        } else if (status === 404) {
          this.loadError = 'This shared project could not be found.'
        } else {
          this.loadError = 'Could not load this shared project.'
        }
      } finally {
        this.isLoading = false
      }
    },

    async fetchTradies() {
      if (!this.projectId) return
      this.isLoading = true
      this.tokenExpiryDates = {}
      try {
        await this.getTradiesByProjectId({
          projectId: this.projectId,
          limit: this.perPage,
          page: this.currentPage,
          search: this.search,
        })
        applyTokenExpiryFromListTradies(this, this.tradies)
        // fetchTradies wiped tokenExpiryDates; re-hydrate the band's rows too.
        applyTokenExpiryFromListTradies(this, this.attentionTradiesSource)
      } catch (err) {
        // non-fatal: keep current state
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Loads the shared project's tradies without a search filter and caches
     * them so the Needing Attention band reflects every flagged tradie on the
     * project, not just those on the main Tradies table's current page.
     */
    async fetchAttentionTradies() {
      if (!this.projectId) return
      try {
        const res = await this.$api.get(`projects/${this.projectId}`, {
          params: { limit: 100, page: 1 },
        })
        const list =
          (res &&
            res.data &&
            res.data.data &&
            res.data.data.tradies &&
            res.data.data.tradies.data) ||
          []
        this.attentionTradiesSource = Array.isArray(list) ? list : []
        applyTokenExpiryFromListTradies(this, this.attentionTradiesSource)
      } catch (err) {
        // non-fatal: the band falls back to the loaded page (see attentionTradiesAll)
      }
    },

    viewTradie(item) {
      if (!item || !item.can_access) return
      if (!this.$router) return
      this.$router.push({
        name: 'projects.shared-view-tradie',
        params: {
          shareId: String(this.shareId),
          tradieId: String(item.id),
        },
      })
    },

    getRowClass(item) {
      if (!item) return ''
      const base = 'cursor-pointer'
      if (!item.can_access) return `${base} row-locked`
      return base
    },

    gotoProjects() {
      this.$router.push({ name: 'projects.list-page' })
    },

    formatExpiryDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    },

    isExpiringSoon(expiryDate, days) {
      if (!expiryDate) return false
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const expiry = new Date(expiryDate)
      expiry.setHours(0, 0, 0, 0)
      const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24))
      return diffDays >= 0 && diffDays <= days
    },

    getTooltipText(value) {
      if (!value) return ''
      return String(value).split(',').join('<br/>')
    },

    shortenString(value, limit = 12) {
      if (!value) return ''
      const str = String(value)
      return str.length > limit ? `${str.substring(0, limit)}...` : str
    },

    isTradieSelected(tradieId) {
      return this.selectedTradies.includes(tradieId)
    },

    toggleTradieSelection(tradieId) {
      if (this.isTradieSelected(tradieId)) {
        this.selectedTradies = this.selectedTradies.filter(
          (id) => id !== tradieId,
        )
      } else {
        this.selectedTradies.push(tradieId)
      }
    },

    async fetchAllAccessibleTradieIds() {
      const meta = this.tradiesMeta || {}
      const total = meta.total != null ? meta.total : this.rows
      if (!total) return []
      const perPage = this.perPage
      const lastPage =
        meta.last_page != null
          ? meta.last_page
          : Math.max(1, Math.ceil(total / perPage))

      const paramsBase = { limit: perPage }
      if (this.search) paramsBase.search = this.search

      const tasks = []
      for (let p = 1; p <= lastPage; p++) {
        tasks.push(
          this.$api.get(`projects/${this.projectId}`, {
            params: { ...paramsBase, page: p },
          }),
        )
      }
      const responses = await Promise.all(tasks)
      const ids = []
      responses.forEach((axiosRes) => {
        const list =
          axiosRes &&
          axiosRes.data &&
          axiosRes.data.data &&
          axiosRes.data.data.tradies &&
          Array.isArray(axiosRes.data.data.tradies.data)
            ? axiosRes.data.data.tradies.data
            : []
        list.forEach((tradie) => {
          if (tradie && tradie.can_access && tradie.id != null) {
            ids.push(tradie.id)
          }
        })
      })
      return [...new Set(ids)]
    },

    async toggleSelectAllTradies() {
      if (this.selectAllTradiesBusy || this.isLoading) return
      this.selectAllTradiesBusy = true
      try {
        const allIds = await this.fetchAllAccessibleTradieIds()
        if (!allIds.length) return
        const allSelected = allIds.every((id) =>
          this.selectedTradies.includes(id),
        )
        if (allSelected) {
          this.selectedTradies = this.selectedTradies.filter(
            (id) => !allIds.includes(id),
          )
        } else {
          allIds.forEach((id) => {
            if (!this.selectedTradies.includes(id)) {
              this.selectedTradies.push(id)
            }
          })
        }
      } catch (e) {
        this.showToast(
          'Error',
          'Could not load all tradies for this list. Try again.',
          'danger',
        )
      } finally {
        this.selectAllTradiesBusy = false
      }
    },

    async downloadTradies() {
      if (!this.canDownloadTradieFiles) return

      if (this.selectedTradies.length === 0) {
        this.showToast(
          'Warning',
          'Please select at least one tradie to download',
          'warning',
        )
        return
      }
      // Snapshot before any await — `project` is a shared store getter that
      // gets cleared the moment any navigation re-dispatches getProject()
      // (see store/modules/project.js), so reading it after the awaits below
      // can pick up null/another project's data instead of this one's.
      const projectNameSnapshot =
        this.project && this.project.name ? this.project.name : 'Unknown'
      try {
        this.showToast('Success', 'Downloading tradie files!')
        const tradieIdsString = this.selectedTradies.join(',')
        // project_id scopes out files archived on THIS project specifically —
        // a file archived here can still be visible (and included) via a
        // different project the tradie is on where it isn't archived.
        const { data: fileIdsResponse } = await this.$api.get(
          '/tradie-file-ids',
          {
            params: { tradie_ids: tradieIdsString, project_id: this.projectId },
          },
        )
        const tradiesPayload = this.selectedTradies.map((id) => ({ id }))
        const fileIds = fileIdsResponse.data
        const { data: zip } = await this.$api.get('download-bulk-files', {
          params: {
            files: fileIds,
            tradies: JSON.stringify(tradiesPayload),
            project_id: this.projectId,
          },
          responseType: 'blob',
        })
        const filename = `Project_${projectNameSnapshot}_Tradies_Files.zip`
        const url = URL.createObjectURL(
          new Blob([zip], { type: 'application/zip' }),
        )
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        this.showToast(
          'Success',
          `Downloaded ${fileIds.length} files from ${this.selectedTradies.length} tradies!`,
        )
      } catch (err) {
        this.showToast('Error', 'Failed to download files', 'danger')
        console.error('Download error:', err)
      }
    },

    showToast(title, message, variant = 'success') {
      this.$bvToast.toast(message, {
        title,
        autoHideDelay: 3000,
        variant,
      })
    },

    canShowTradieField(item) {
      return !!(item && item.can_access)
    },

    // API-supplied list of why this tradie needs attention on the project
    // (missing trade docs, expired/expiring docs, missing/unapproved White
    // Card or photo ID). Empty when they're clear.
    needAttentionReasons(item) {
      return Array.isArray(item && item.need_attention_reasons)
        ? item.need_attention_reasons
        : []
    },

    /** True when a tradie row is locked because no token is assigned. */
    isTradieTokenLocked(item) {
      return !!(item && !item.can_access)
    },

    /**
     * A sharee can't assign a token themselves — the tokens belong to the
     * company that shared the project — so the locked-row copy points them at
     * that company instead of the owner-view's "Assign token to view".
     */
    tradieLockMessage() {
      const sharer =
        (this.share && this.share.sharer_company_name) || 'the sharing company'
      return `Locked - Contact ${sharer} to request access`
    },

    /** Plain CSS % centering (`_table.scss`) lands on the row's own box, not
     * the full table width, so it overlaps the name instead of sitting
     * centred. Measure and place it manually, same approach as ViewPage. */
    positionLockOverlays() {
      if (!this.$el) return
      this.$el.querySelectorAll('.lock-overlay').forEach((overlay) => {
        const tr = overlay.closest('tr')
        const tableEl = overlay.closest('.tradies-table')
        if (!tr || !tableEl) return

        overlay.style.left = '0px'
        overlay.style.top = '0px'
        overlay.style.width = 'max-content'
        overlay.style.maxWidth = 'none'
        overlay.style.transform = 'none'
        const originRect = overlay.getBoundingClientRect()
        const trRect = tr.getBoundingClientRect()
        const tableRect = tableEl.getBoundingClientRect()

        const tableCenterX = tableRect.left + tableRect.width / 2
        overlay.style.left = `${
          tableCenterX - originRect.width / 2 - originRect.left
        }px`
        overlay.style.top = `${
          trRect.top -
          originRect.top +
          trRect.height / 2 -
          originRect.height / 2
        }px`
      })
    },
  },

  mounted() {
    this.fetchAll()
    this.$nextTick(() =>
      requestAnimationFrame(() => this.positionLockOverlays()),
    )
    window.addEventListener('resize', this.positionLockOverlays)
    window.addEventListener('resize', this.onResize)
  },

  updated() {
    this.$nextTick(() => this.positionLockOverlays())
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.positionLockOverlays)
    window.removeEventListener('resize', this.onResize)
  },
}
</script>

<style lang="scss" scoped>
.table-wrapper {
  position: relative;
  overflow: visible;
  max-width: 100%;
}

// "Reason" column (Needing Attention band): a bulleted list of why the tradie
// needs attention. Cap the column so it doesn't crowd out Roles.
/deep/ .attention-reason-cell {
  max-width: 260px;
  white-space: normal;
}
.attention-reasons {
  margin: 0;
  padding-left: 1.1em;
  list-style: disc outside;
}
.attention-reason {
  font-size: 0.8rem;
  line-height: 1.3;
  white-space: normal;
}

/deep/ .tradies-table {
  overflow: visible !important;
}

/* Keep every tradies table contained within its column at ALL widths: it
   scrolls horizontally inside its own `.table-responsive` box rather than
   pushing past the card. */
/deep/ .table-responsive {
  overflow-x: auto !important;
  max-width: 100%;
}

/deep/ .tradies-table tbody tr.row-locked td {
  overflow: visible !important;
}

/deep/ .tradies-table .row-locked .lock-overlay,
/deep/ .tradies-table .row-locked .lock-overlay span {
  color: #999 !important;
}

/deep/ .tradies-table .row-locked .lock-overlay {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  pointer-events: none;
  font-size: 1.1rem;
  font-weight: 600;
  white-space: nowrap;
  width: max-content;
  max-width: none;
  opacity: 1 !important;
}

/deep/ .tradies-table .lock-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
  margin-right: 8px;
}

/* Table column titles larger than the row text below them. em (not rem) so
   this scales with .tradies-table's own font-size — including the 0.78rem
   it drops to on phones. */
/deep/ .tradies-table th {
  font-size: 1.2em;
}

/* Section titles ("Tradies:", "Needing Attention:", "Expiring Files (in 30
   days):") — larger than the regular body text. */
.section-title {
  font-size: 1.2rem;
}

/* "Expiring Files (in 30 days):" is forced to one line on phones
   (.title-nowrap-mobile) — scale all titles back a bit below sm so the
   longest one still fits a 320px screen. */
@media (max-width: 575.98px) {
  .section-title {
    font-size: 1.05rem;
  }
}

.cell-content-wrapper {
  position: relative;
}

.lock-overlay {
  display: inline-flex;
  align-items: center;
  font-size: 1.1rem;
  font-weight: 600;
  color: #6c757d;
}

.lock-icon {
  width: 32px;
  height: 32px;
  margin-right: 8px;
}

.bg-highlight {
  background-color: #fff8e1;
  border-radius: 4px;
}

.read-only-banner {
  background-color: #eef4ff;
  border-color: #cfe0ff;
  color: #24428a;
}

.project-approved-tick {
  vertical-align: -0.125em;
  font-size: 1rem;
}

/deep/ th.project-approved-th,
/deep/ td.project-approved-td {
  width: 28px;
  padding-left: 8px;
  padding-right: 0;
  text-align: center;
}

/deep/ .cursor-pointer {
  cursor: pointer;
}

/deep/ .cursor-pointer td {
  vertical-align: middle;
}

/deep/ .row-locked {
  cursor: not-allowed;
}

/* Section labels (e.g. "Expiring Files (in 30 days):") stay on a single
   line on phones, where they get a full-width row of their own. From md up
   the label column is sized to fit the text on one line anyway. */
.title-nowrap-mobile {
  white-space: nowrap;
}
@media (min-width: 768px) {
  .title-nowrap-mobile {
    white-space: normal;
  }
}

.project-name-detail-label {
  margin-right: 0.5rem;
  font-size: 1.2rem;
}

.project-name-detail-value {
  min-width: 0;
  font-size: 1.2rem;
}

.project-shared-by {
  font-size: 0.9rem;
}

/* Header action button (Back to Projects). Desktop keeps the original
   full-width-up-to-220px pill sizing. */
.header-action-btn {
  max-height: 64px;
  max-width: 220px;
  width: 100%;
  min-height: 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #c8d1dc !important;
  border-radius: 0.5rem;
  font-weight: 600;
  line-height: 1.2;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  transition: background-color 0.18s ease, border-color 0.18s ease,
    box-shadow 0.18s ease, transform 0.18s ease;
}

.header-action-btn:hover,
.header-action-btn:focus {
  background-color: #f8fafc !important;
  border-color: #8794a3 !important;
  color: #1f2d3d !important;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.1);
  transform: translateY(-1px);
}

.header-action-btn:focus {
  box-shadow: 0 0 0 0.2rem rgba(0, 108, 255, 0.14),
    0 4px 10px rgba(15, 23, 42, 0.1);
}

.header-action-btn:active {
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  transform: translateY(0);
}

/* Small screens (< md): the header button collapses to a compact,
   icon-only button. The label reappears from md up. */
@media (max-width: 767.98px) {
  .header-action-btn {
    width: auto;
    max-width: none;
    max-height: none;
    /* The global .btn rule fixes buttons at 56px tall — let this compact
       icon button size to its icon instead. */
    height: auto;
    min-height: 0;
    padding: 0.4rem 0.6rem;
    font-size: 0.875rem;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .header-action-icon {
    font-size: 1.2rem;
    vertical-align: middle;
  }
}

/* Tablet range (md): the labelled pill is back but its md="4" column gets
   narrow — let the button grow to contain its text and tighten
   font/padding so it usually fits on one line anyway. */
@media (min-width: 768px) and (max-width: 991.98px) {
  .header-action-btn {
    max-height: none;
    font-size: 0.875rem;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
}

/* Tradies toolbar action button (Download). Desktop keeps the original
   48px-tall button. */
.tradie-action-btn {
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  border-radius: 0.5rem;
  border-width: 1px;
  font-weight: 600;
  line-height: 1.2;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
  transition: background-color 0.18s ease, border-color 0.18s ease,
    box-shadow 0.18s ease, transform 0.18s ease;
}

.tradie-action-btn:hover,
.tradie-action-btn:focus {
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.14);
  transform: translateY(-1px);
}

.tradie-action-btn:focus {
  box-shadow: 0 0 0 0.2rem rgba(0, 108, 255, 0.16),
    0 4px 10px rgba(15, 23, 42, 0.14);
}

.tradie-action-btn:active {
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.1);
  transform: translateY(0);
}

/* Neutralise the toolbar row's default Bootstrap -15px gutter margins at
   every width — its wrapping <b-container fluid> has that padding zeroed
   out (class="px-0"), so with nothing to cancel it out the raw negative
   margin pulls the search box ~15px left of the table below it. */
.tradie-toolbar-row {
  margin-left: 0 !important;
  margin-right: 0 !important;
}

/* Phones & small tablets: the action button gets a short label and sits
   flush-left, aligned with the search field. */
@media (max-width: 767.98px) {
  .tradie-toolbar-actions {
    padding-left: 15px !important;
    padding-right: 15px !important;
    column-gap: 0.5rem;
    row-gap: 0.5rem;
  }
  .tradie-toolbar-actions .tradie-action-btn {
    flex: 1 1 calc(50% - 0.25rem);
    min-width: 0;
    height: 42px;
    margin: 0 !important;
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
    font-size: 0.78rem;
    display: flex !important;
    align-items: center;
    justify-content: center;
    gap: 0.3rem;
  }

  .tradie-toolbar-actions .tradie-action-btn .b-icon {
    font-size: 0.9rem !important;
    flex-shrink: 0;
  }

  /* A lone Download button shouldn't be stretched into a full-width slab —
     let it size to its label and sit on the left, aligned with the search
     field. */
  .tradie-toolbar-actions {
    justify-content: flex-start !important;
  }
  .tradie-toolbar-actions .tradie-action-btn {
    flex: 0 0 auto;
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }

  .tradie-toolbar-actions--supervisor {
    justify-content: flex-start !important;
  }

  /* Compact the search field so it matches the smaller button, and pin its
     left/right gutter so the label + input line up with the "Tradies:"
     label and the table. */
  .tradie-search {
    padding-left: 15px !important;
    padding-right: 15px !important;
  }
  .tradie-search b {
    font-size: 0.85rem;
  }
  .tradie-search /deep/ .form-control {
    height: 40px;
    padding: 0.25rem 0.6rem;
    font-size: 0.85rem;
  }

  /* Needing Attention's search box, nudged to land under the table's row
     text (same tuning as the desktop rule below). */
  .band-search {
    padding-left: 15px !important;
  }
}

/* Tablet (md, 768-991px): give the search block clearance from the
   "Tradies:" label column and reclaim the pr-md-5 dead space. */
@media (min-width: 768px) and (max-width: 991.98px) {
  .tradie-search {
    padding-right: 1rem !important;
  }
}

/* Phones: let the project title take the free space (actions size to
   content) and keep it on a single line, ellipsizing long names. */
@media (max-width: 767.98px) {
  .project-header-row {
    align-items: center;
  }
  .project-title-col {
    min-width: 0;
  }
  .project-actions-col {
    flex: 0 0 auto;
    width: auto;
  }
  .project-title {
    font-size: 1.5rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 0;
  }
  .project-shared-by {
    font-size: 0.8rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

/* ------------------------------------------------------------------ *
 * Tradies table: compact + horizontally scrollable on small screens.
 *
 *   1. Columns are progressively hidden via d-none/d-*-table-cell in the
 *      `tradieFields` computed (only the essentials show on phones, the
 *      rest reappear as the viewport grows back into the desktop table).
 *   2. Whatever columns ARE shown never wrap into tall, cramped rows —
 *      cells stay on a single line and the `.table-responsive` wrapper
 *      scrolls horizontally instead.
 * The desktop table (>= lg) is left untouched.
 * ------------------------------------------------------------------ */
@media (max-width: 991.98px) {
  /deep/ .table-responsive {
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
  }
  /deep/ .tradies-table th,
  /deep/ .tradies-table td {
    white-space: nowrap;
  }
  /* Let the (widest) name column wrap instead of forcing a horizontal
     scroll from long names. */
  /deep/ .tradies-table td.tradie-name-cell {
    white-space: normal;
    min-width: 5.5rem;
  }
  /* The lock banner is the one element allowed to wrap within its row. */
  /deep/ .tradies-table tr.row-locked .lock-overlay,
  /deep/ .tradies-table tr.row-locked .lock-overlay span {
    white-space: normal;
  }
}

/* From the md desktop table (768px) up to ~1400px the tradie tables are tight
   in their card — the Needing Attention band also carries an extra "Reason"
   column. Shrink font + padding and let cells WRAP so every column stays
   visible without a horizontal scrollbar. Declared after the
   `max-width: 991.98px` rules so its `white-space: normal` wins over their
   nowrap-and-scroll in the overlapping 768–992 range. */
@media (min-width: 768px) and (max-width: 1399.98px) {
  /deep/ .tradies-table {
    font-size: 0.72rem;
  }
  /deep/ .tradies-table th,
  /deep/ .tradies-table td {
    padding: 0.3rem 0.3rem;
    white-space: normal;
    /* Long unbroken tokens (emails) wrap instead of forcing the table wide. */
    overflow-wrap: anywhere;
  }
  /deep/ .attention-reason-cell {
    max-width: 200px;
  }
  .attention-reason {
    font-size: 0.68rem;
    line-height: 1.15;
  }
}

@media (max-width: 575.98px) {
  /deep/ .tradies-table {
    font-size: 0.78rem;
  }
  /deep/ .tradies-table th,
  /deep/ .tradies-table td {
    padding: 0.4rem 0.35rem;
  }

  /* Confine the "Locked - Assign token" banner to the row so it can't
     spill past the viewport (the JS centring is overridden here). */
  /deep/ .tradies-table tr.row-locked .lock-overlay {
    left: 0 !important;
    right: 0 !important;
    top: 50% !important;
    width: auto !important;
    max-width: 100% !important;
    transform: translateY(-50%) !important;
    white-space: normal !important;
    justify-content: center;
    text-align: center;
    padding: 0 6px;
    font-size: 0.85rem;
  }
  /deep/ .tradies-table tr.row-locked .lock-overlay span {
    display: none;
  }
  /deep/ .tradies-table tr.row-locked .lock-overlay .lock-icon {
    margin-right: 0 !important;
    width: 20px;
    height: 20px;
  }

  .tradies-pagination {
    font-size: 0.85rem !important;
  }
  .tradies-pagination /deep/ .page-link {
    padding: 0.25rem 0.5rem !important;
  }

  .tradie-count {
    font-size: 0.8rem;
    margin-top: 0.35rem;
    margin-bottom: 0.5rem;
  }
}

/* Inter-section divider — visible horizontal rule with breathing room. */
.section-separator {
  height: 0;
  border: 0;
  border-top: 1px solid #e9ecef;
  margin: 2rem 0;
}

/* Explicit, consistent gap above the pagination on all tables here —
   matches Expiring Files' own .tradies-pagination rule in
   ExpiringTradieList.vue. */
.tradies-pagination {
  margin-top: 0.5rem;
}

.mobile-tradie-list {
  margin-top: 0.5rem;
}

/* On desktop (md+), indent the sections so their labels align with the
   "Project" label above them in the card body. */
@media (min-width: 768px) {
  .attention-row,
  .mobile-flush-row,
  .tradie-row {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  /* Expiring Files' content is <expiring-tradie-list>, which wraps itself
     in its own <b-container fluid> (Bootstrap's default 0.9375rem gutter
     on top of the 1rem above). Needing Attention renders its table
     directly with no such wrapper, and Tradies' own <b-container fluid>
     has that gutter zeroed out (class="px-0") — match it directly so all
     sections line up. */
  .attention-cards-col,
  .tradie-content-col {
    padding-left: 0.9375rem;
    padding-right: 0.9375rem;
  }

  /* Needing Attention's search box, nudged to land under "Full Name". */
  .band-search {
    padding-left: 15px !important;
  }
}

/* On phones, pull the section bands close to the viewport edges so the
   cards fill the width instead of leaving a wide gap. */
@media (max-width: 767.98px) {
  .attention-row,
  .mobile-flush-row,
  .tradie-row {
    margin-left: -0.5rem;
    margin-right: -0.5rem;
  }
  .attention-row > [class*='col'],
  .mobile-flush-row > [class*='col'],
  .tradie-row > [class*='col'] {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
  /* Section labels are .col-12 on phones, so the global 30px col-12 margin
     opens a big gap between the label and its section content — collapse
     it. */
  .mobile-flush-row > .title-nowrap-mobile {
    margin-bottom: 0;
  }
  .attention-cards-col .mobile-tradie-list {
    margin-top: 0.25rem;
  }

  .section-separator {
    margin: 1rem 0;
  }
  .mobile-tradie-list {
    margin-top: 0.25rem;
  }
  .mobile-tradie-list .py-4 {
    padding-top: 0.75rem !important;
    padding-bottom: 0.75rem !important;
  }
  .tradie-count {
    margin-top: 0.25rem !important;
  }

  /* Tighten every stacked tradie row so name + email fit down to 320px. */
  .mobile-tradie-item {
    gap: 0.25rem;
    padding: 0.6rem 0.4rem 0.6rem 0.55rem;
  }
  .mobile-tradie__select-btn {
    height: 28px;
    width: 28px;
  }
  /* The Needing Attention band mirrors its desktop table, which has no Select
     column — drop the select checkbox from its mobile rows too so the narrow
     band isn't two controls wide. */
  .attention-cards-col .mobile-tradie__select {
    display: none;
  }
}

/* ------------------------------------------------------------------ *
 * Mobile tradie rows (< md): stacked "card" rows matching the projects
 * list pattern — a bold name, a small meta line, an inline select, and a
 * divider — instead of a scrolling table. Hidden on desktop via
 * `d-md-none`, so these styles never touch the >= md table.
 * ------------------------------------------------------------------ */
.mobile-tradie-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.65rem 0.6rem 0.65rem 0.85rem;
  border-bottom: 1px solid #e1e6ea;
  background: #fff;
}

.mobile-tradie-item.row-locked {
  background: #f5f5f5;
}

.mobile-tradie-item.is-selected {
  background: #eef9f1;
}

.mobile-tradie__tick {
  flex-shrink: 0;
  font-size: 1rem;
}

// Name + the "project approved" tick sit on one line, tick to the right of
// the (truncated) name — keeps every row's name flush-left whether approved
// or not, instead of the tick being a column that shifts the name across.
.mobile-tradie__name-row {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
}
.mobile-tradie__name-row .mobile-tradie__name {
  flex: 0 1 auto;
}

.mobile-tradie__body {
  flex: 1 1 auto;
  min-width: 0;
}

.mobile-tradie__name {
  display: block;
  font-weight: 600;
  font-size: 0.875rem;
  color: #364052;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-tradie__meta {
  font-size: 0.72rem;
  color: #8b95a3;
  margin-top: 0.15rem;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// The locked message ("Locked - Contact your business admin to unlock") is
// longer than one line at 320px; unlike the email/trades meta it can't be
// meaningfully truncated, so let it wrap instead of clipping.
.mobile-tradie__meta--lock {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
}

// Needing Attention band, mobile rows: the reasons list is the point of the
// band, so show it in full (wrapping) rather than clipped on the meta line.
.mobile-tradie__reasons {
  margin: 0.25rem 0 0;
  padding-left: 1.05em;
  list-style: disc outside;
  font-size: 0.72rem;
  line-height: 1.3;
  color: #c5221f;
}
.mobile-tradie__reasons li {
  white-space: normal;
}

.mobile-tradie__lock-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.mobile-tradie__select {
  flex-shrink: 0;
}

.mobile-tradie__select-btn {
  height: 32px;
  width: 32px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid grey;
}
</style>
